"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.planMigration = planMigration;
exports.stockValue = stockValue;
const money_1 = require("../money");
const document_rules_1 = require("../posting/document-rules");
const MEMO = 'رصيد مرحَّل من البرنامج السابق';
function amount(value) {
    return value.toFixed(money_1.PRESENTATION_SCALE);
}
/**
 * المصدر المرجّح لفرق الميزان: صف بقيمة الفرق نفسها (ناقص أو مكرر)، أو بنصفه (مقيَّد فى
 * الطرف الخطأ فيُضاعف الفرق)، وإلا فالفرق القابل للقسمة على ٩ علامة تبديل رقمين.
 */
function trialBalanceSuspects(rows, difference) {
    const gap = difference.abs();
    const sideOf = (row) => money_1.Money.of(row.debit).isPositive() ? money_1.Money.of(row.debit) : money_1.Money.of(row.credit);
    const exact = rows.filter((row) => sideOf(row).eq(gap));
    if (exact.length > 0) {
        return {
            detail: 'الفرق ' +
                amount(gap) +
                ' يساوى رصيد صف بعينه — غالبًا صف ناقص من الطرف الآخر أو مكرر فى هذا الطرف.',
            suspects: exact.slice(0, 5).map((row) => suspect('trial_balance', row)),
        };
    }
    const half = gap.div(2);
    const flipped = rows.filter((row) => sideOf(row).eq(half));
    if (flipped.length > 0) {
        return {
            detail: 'الفرق ضعف رصيد صف بعينه — غالبًا رصيد قُيِّد فى الطرف الخطأ (مدين بدل دائن أو العكس).',
            suspects: flipped.slice(0, 5).map((row) => suspect('trial_balance', row)),
        };
    }
    const cents = gap.mul(100).round(0);
    if (cents.isPositive() && cents.toDecimal().mod(9).isZero()) {
        return {
            detail: 'الفرق ' +
                amount(gap) +
                ' يقبل القسمة على ٩ — علامة معتادة لتبديل رقمين عند الإدخال (٥٤ بدل ٤٥).',
            suspects: largest(rows, (row) => sideOf(row), 5).map((row) => suspect('trial_balance', row)),
        };
    }
    return {
        detail: 'مجموع المدين لا يساوى مجموع الدائن بفرق ' +
            amount(gap) +
            '. راجع الصفوف الأكبر أثرًا أو صفوفًا لم تُرفع.',
        suspects: largest(rows, (row) => sideOf(row), 5).map((row) => suspect('trial_balance', row)),
    };
}
function suspect(kind, row) {
    return { kind, rowId: row.rowId, label: row.label };
}
function largest(rows, value, count) {
    return [...rows].sort((a, b) => value(b).abs().cmp(value(a).abs())).slice(0, count);
}
/**
 * مطابقة دفتر مساعد مع إجماليه فى الميزان. المصدر المرجّح: طرف رصيده يساوى الفرق
 * (ناقص أو زائد)، أو أطراف بأرصدة عكس الطبيعة، وإلا الأكبر أثرًا.
 */
function subledgerCheck(code, kind, hasTrialBalance, control, detail, rows, names) {
    if (!hasTrialBalance) {
        return {
            code,
            status: 'na',
            expected: null,
            actual: detail,
            difference: null,
            detail: rows.length
                ? 'لا ميزان فى الدفعة: ' +
                    names.detail +
                    ' تُقيَّد مقابل حساب فروق الأرصدة الافتتاحية حتى يُستكمل الميزان.'
                : 'لا ميزان ولا ' + names.detail + ' فى الدفعة.',
            suspects: [],
        };
    }
    const difference = detail.sub(control).normalize();
    if (difference.isZero()) {
        return {
            code,
            status: 'ok',
            expected: control,
            actual: detail,
            difference,
            detail: rows.length === 0 && control.isZero()
                ? 'لا ' + names.detail + ' ولا رصيد لـ' + names.control + ' فى الميزان.'
                : 'مجموع ' + names.detail + ' يساوى رصيد ' + names.control + ' فى الميزان.',
            suspects: [],
        };
    }
    if (rows.length === 0) {
        return {
            code,
            status: 'fail',
            expected: control,
            actual: detail,
            difference,
            detail: 'الميزان يحمل رصيدًا لـ' +
                names.control +
                ' (' +
                amount(control) +
                ') ولا تفصيل له — ارفع ملف ' +
                names.detail +
                ' بأرصدتهم.',
            suspects: [],
        };
    }
    const gap = difference.abs();
    const exact = rows.filter((row) => row.amount.abs().eq(gap));
    if (exact.length > 0) {
        return {
            code,
            status: 'fail',
            expected: control,
            actual: detail,
            difference,
            detail: 'الفرق ' +
                amount(gap) +
                ' يساوى رصيد طرف بعينه — غالبًا ' +
                (difference.isPositive()
                    ? 'مكرر أو ليس من هذا الحساب.'
                    : 'ناقص من الملف أو مقلوب الإشارة.'),
            suspects: exact.slice(0, 5).map((row) => suspect(kind, row)),
        };
    }
    const reversed = rows.filter((row) => row.amount.isNegative());
    return {
        code,
        status: 'fail',
        expected: control,
        actual: detail,
        difference,
        detail: 'مجموع ' +
            names.detail +
            ' (' +
            amount(detail) +
            ') لا يساوى رصيد ' +
            names.control +
            ' فى الميزان (' +
            amount(control) +
            ')' +
            (reversed.length > 0
                ? ' — وفيه ' + reversed.length + ' بأرصدة عكس الطبيعة، راجع إشاراتها أولاً.'
                : ' — راجع الأرصدة الأكبر أو أطرافًا لم تُرفع.'),
        suspects: (reversed.length > 0 ? reversed : largest(rows, (row) => row.amount, 5))
            .slice(0, 5)
            .map((row) => suspect(kind, row)),
    };
}
/** الحساب الوحيد من دفتر مساعد يحمل رصيدًا فى الميزان، أو null (لا شىء أو أكثر من حساب). */
function soleAccount(nets, codes) {
    const withBalance = codes.filter((code) => !(nets.get(code) ?? money_1.Money.zero()).isZero());
    return withBalance.length === 1 ? withBalance[0] : null;
}
function planMigration(input) {
    const { currency } = input;
    const subledgerOf = (code) => input.accounts.get(code)?.subledger ?? null;
    // صافى كل حساب فى الميزان (موجب مدين)
    const nets = new Map();
    let trialBalanceDebit = money_1.Money.zero();
    let trialBalanceCredit = money_1.Money.zero();
    for (const row of input.trialBalance) {
        trialBalanceDebit = trialBalanceDebit.add(row.debit);
        trialBalanceCredit = trialBalanceCredit.add(row.credit);
        nets.set(row.accountCode, (nets.get(row.accountCode) ?? money_1.Money.zero()).add(row.debit).sub(row.credit));
    }
    const codesOf = (subledger) => [...nets.keys()].filter((code) => subledgerOf(code) === subledger);
    const sumNets = (codes) => money_1.Money.sum(codes.map((code) => nets.get(code) ?? money_1.Money.zero()));
    const customerCodes = codesOf('customer');
    const supplierCodes = codesOf('supplier');
    const inventoryCodes = codesOf('inventory');
    const controlReceivables = sumNets(customerCodes);
    const controlPayables = sumNets(supplierCodes).negate();
    const controlInventory = sumNets(inventoryCodes);
    const customerRows = input.customers.map((row) => ({ ...row, amount: money_1.Money.of(row.balance) }));
    const supplierRows = input.suppliers.map((row) => ({ ...row, amount: money_1.Money.of(row.balance) }));
    const stockRows = input.stock.map((row) => ({ ...row, amount: money_1.Money.of(row.value) }));
    const receivables = money_1.Money.sum(customerRows.map((row) => row.amount));
    const payables = money_1.Money.sum(supplierRows.map((row) => row.amount));
    const inventory = money_1.Money.sum(stockRows.map((row) => row.amount));
    const checks = [];
    const tbDifference = trialBalanceDebit.sub(trialBalanceCredit).normalize();
    if (!input.hasTrialBalance) {
        checks.push({
            code: 'TRIAL_BALANCE_BALANCED',
            status: 'na',
            expected: null,
            actual: null,
            difference: null,
            detail: 'الدفعة بلا ميزان مراجعة.',
            suspects: [],
        });
    }
    else if (tbDifference.isZero()) {
        checks.push({
            code: 'TRIAL_BALANCE_BALANCED',
            status: trialBalanceDebit.isZero() ? 'fail' : 'ok',
            expected: trialBalanceDebit,
            actual: trialBalanceCredit,
            difference: tbDifference,
            detail: trialBalanceDebit.isZero()
                ? 'الميزان بلا أرصدة — لا شىء يُرحَّل منه.'
                : 'مجموع المدين يساوى مجموع الدائن.',
            suspects: [],
        });
    }
    else {
        checks.push({
            code: 'TRIAL_BALANCE_BALANCED',
            status: 'fail',
            expected: trialBalanceDebit,
            actual: trialBalanceCredit,
            difference: tbDifference,
            ...trialBalanceSuspects(input.trialBalance, tbDifference),
        });
    }
    checks.push(subledgerCheck('RECEIVABLES_MATCH', 'customers', input.hasTrialBalance, controlReceivables, receivables, customerRows, { control: 'حساب العملاء', detail: 'أرصدة العملاء' }), subledgerCheck('PAYABLES_MATCH', 'suppliers', input.hasTrialBalance, controlPayables, payables, supplierRows, { control: 'حساب الموردين', detail: 'أرصدة الموردين' }), subledgerCheck('INVENTORY_MATCH', 'items', input.hasTrialBalance, controlInventory, inventory, stockRows, { control: 'حساب المخزون', detail: 'قيمة الأصناف (الكمية × التكلفة)' }));
    // السطور: الميزان بلا حسابات الدفاتر المساعدة، ثم تفصيلها
    const lines = [];
    const controlled = new Set([...customerCodes, ...supplierCodes, ...inventoryCodes]);
    for (const [code, net] of [...nets].sort(([a], [b]) => a.localeCompare(b))) {
        if (controlled.has(code) || net.isZero())
            continue;
        const extras = input.lineExtras?.get(code) ?? {};
        const options = { memo: MEMO, ...extras };
        lines.push(net.isPositive()
            ? (0, document_rules_1.debitLine)(code, net, currency, options)
            : (0, document_rules_1.creditLine)(code, net.abs(), currency, options));
    }
    const receivableAccount = soleAccount(nets, customerCodes) ?? input.receivableAccount;
    for (const row of customerRows) {
        if (row.amount.isZero())
            continue;
        const options = { memo: MEMO, counterparty: { type: 'customer', id: row.partyKey } };
        lines.push(row.amount.isPositive()
            ? (0, document_rules_1.debitLine)(receivableAccount, row.amount, currency, options)
            : (0, document_rules_1.creditLine)(receivableAccount, row.amount.abs(), currency, options));
    }
    const payableAccount = soleAccount(nets, supplierCodes) ?? input.payableAccount;
    for (const row of supplierRows) {
        if (row.amount.isZero())
            continue;
        const options = { memo: MEMO, counterparty: { type: 'supplier', id: row.partyKey } };
        lines.push(row.amount.isPositive()
            ? (0, document_rules_1.creditLine)(payableAccount, row.amount, currency, options)
            : (0, document_rules_1.debitLine)(payableAccount, row.amount.abs(), currency, options));
    }
    const stock = [];
    const inventoryByAccount = new Map();
    for (const row of input.stock) {
        const quantity = money_1.Money.of(row.quantity);
        if (!quantity.isPositive())
            continue;
        const value = money_1.Money.of(row.value);
        stock.push({
            itemKey: row.itemKey,
            inventoryAccountCode: row.inventoryAccountCode,
            quantity,
            value,
        });
        inventoryByAccount.set(row.inventoryAccountCode, (inventoryByAccount.get(row.inventoryAccountCode) ?? money_1.Money.zero()).add(value));
    }
    for (const [code, value] of [...inventoryByAccount].sort(([a], [b]) => a.localeCompare(b))) {
        if (value.isZero())
            continue;
        lines.push((0, document_rules_1.debitLine)(code, value, currency, { memo: MEMO }));
    }
    // دفعة بلا ميزان: سطر فرق يوازن التفصيل وحده
    let openingDifference = money_1.Money.zero();
    if (!input.hasTrialBalance) {
        const net = money_1.Money.sum(lines.map((line) => line.debit))
            .sub(money_1.Money.sum(lines.map((line) => line.credit)))
            .normalize();
        if (!net.isZero()) {
            openingDifference = net;
            const memo = 'فرق أرصدة افتتاحية — دفعة ترحيل بلا ميزان';
            lines.push(net.isPositive()
                ? (0, document_rules_1.creditLine)(input.openingDifferenceAccount, net, currency, { memo })
                : (0, document_rules_1.debitLine)(input.openingDifferenceAccount, net.abs(), currency, { memo }));
        }
    }
    return {
        checks,
        ready: checks.every((check) => check.status !== 'fail'),
        totals: { trialBalanceDebit, trialBalanceCredit, receivables, payables, inventory },
        lines,
        stock,
        openingDifference,
    };
}
/** قيمة رصيد صنف: الإجمالى المُدخل إن وُجد، وإلا الكمية × تكلفة الوحدة مقرَّبة للقرش. */
function stockValue(quantity, unitCost, totalValue) {
    if (totalValue !== null)
        return money_1.Money.of(totalValue).normalize();
    return money_1.Money.of(quantity)
        .mul(unitCost ?? 0)
        .round(money_1.PRESENTATION_SCALE, 'half_up');
}
//# sourceMappingURL=migration.js.map